<script setup lang="ts">
import { type HTMLAttributes, ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { ChevronDown, Search } from 'lucide-vue-next';
import { allCountries } from '@pkg/config/misc/countries';
import { defaultLocale, getLocaleFromLocaleCode } from '@pkg/config/languages';
import { useFormField } from '../form/useFormField';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  class?: HTMLAttributes['class'];
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  error?: string | boolean;
}>();

// Get form field error state automatically
const { error: formError } = useFormField();

// Get current locale for default country
const { locale: localeCode } = useI18n();
const currentLocaleIso = getLocaleFromLocaleCode(localeCode.value || defaultLocale)?.countryIso;
const currentCountry = allCountries.find(country => country.countryIso === currentLocaleIso);

const phoneNumber = ref('');
const selectedCountry = ref(currentCountry || allCountries[0]);
const isDropdownOpen = ref(false);
const searchQuery = ref('');
const focusedIndex = ref(-1);
const dropdownRef = ref<HTMLElement>();
const searchInputRef = ref<HTMLInputElement>();

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string): void;
  (e: 'country-changed', payload: any): void;
  (e: 'input', payload: any): void;
}>();

// Watch for changes and emit the full phone number
watch([selectedCountry, phoneNumber], () => {
  const fullNumber = phoneNumber.value ? `${selectedCountry.value.phoneCode}${phoneNumber.value}` : '';
  emits('update:modelValue', fullNumber);
});

// Handle country change
const handleCountryChange = (country: any) => {
  selectedCountry.value = country;
  isDropdownOpen.value = false;
  focusedIndex.value = -1;
  emits('country-changed', country);
};

// Handle input change
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  phoneNumber.value = target.value.replace(/\D/g, '');
  emits('input', { nationalNumber: phoneNumber.value });
};

// Toggle dropdown
const toggleDropdown = () => {
  if (!props.disabled) {
    isDropdownOpen.value = !isDropdownOpen.value;
    if (isDropdownOpen.value) {
      nextTick(() => {
        searchInputRef.value?.focus();
      });
    }
  }
};

// Handle keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (!isDropdownOpen.value) return;
  
  const countries = filteredCountries.value;
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      focusedIndex.value = Math.min(focusedIndex.value + 1, countries.length - 1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      focusedIndex.value = Math.max(focusedIndex.value - 1, -1);
      break;
    case 'Enter':
      event.preventDefault();
      if (focusedIndex.value >= 0 && focusedIndex.value < countries.length) {
        handleCountryChange(countries[focusedIndex.value]);
      }
      break;
    case 'Escape':
      event.preventDefault();
      isDropdownOpen.value = false;
      focusedIndex.value = -1;
      break;
  }
};

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as Element;
  if (!target.closest('.phone-number-input-wrapper')) {
    isDropdownOpen.value = false;
  }
};

// Add click outside listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Filter countries based on search query
const filteredCountries = computed(() => {
  if (!searchQuery.value) {
    return allCountries;
  }
  return allCountries.filter(country => 
    country.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    country.phoneCode.includes(searchQuery.value)
  );
});


</script>

<template>
  <div class="phone-number-input-wrapper">
    <div class="phone-input-container" :class="{ error: props.error || formError }">
      <!-- Country Dropdown -->
      <div class="country-dropdown-container">
        <button
          type="button"
          class="country-dropdown-button"
          @click="toggleDropdown"
          @keydown="handleKeydown"
          :disabled="props.disabled"
          :aria-expanded="isDropdownOpen"
          aria-haspopup="listbox"
        >
          <span class="country-flag">{{ selectedCountry.flag }}</span>
          <span class="country-code">{{ selectedCountry.phoneCode }}</span>
          <ChevronDown 
            class="dropdown-arrow" 
            :class="{ 'arrow-up': isDropdownOpen }"
          />
        </button>
        
        <!-- Dropdown Menu -->
        <div v-if="isDropdownOpen" class="dropdown-menu" role="listbox" ref="dropdownRef">
          <!-- Search Box -->
          <div class="search-container">
            <div class="search-input-wrapper">
              <Search class="search-icon" />
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search countries..."
                class="search-input"
                ref="searchInputRef"
                @click.stop
                @keydown="handleKeydown"
              />
            </div>
          </div>
          
          <!-- Country List -->
          <div class="country-list">
            <button
              v-for="(country, index) in filteredCountries"
              :key="country.countryIso"
              type="button"
              class="country-item"
              :class="{ 
                'selected': country.countryIso === selectedCountry.countryIso,
                'focused': index === focusedIndex 
              }"
              @click="handleCountryChange(country)"
              @mouseenter="focusedIndex = index"
              role="option"
              :aria-selected="country.countryIso === selectedCountry.countryIso"
            >
              <span class="country-flag">{{ country.flag }}</span>
              <span class="country-code">{{ country.phoneCode }}</span>
              <span class="country-name">{{ country.title }}</span>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Phone Number Input -->
      <input
        v-model="phoneNumber"
        type="tel"
        :placeholder="props.placeholder || 'Enter phone number'"
        :required="props.required"
        :disabled="props.disabled"
        :id="props.id || 'phone-input'"
        :name="props.name || 'phone'"
        class="phone-number-input"
        @input="handleInput"
        autocomplete="tel"
        v-bind="$attrs"
      />
    </div>
  </div>
</template>

<style scoped>
.phone-number-input-wrapper {
  width: 100%;
}

.phone-input-container {
  display: flex;
  width: 100%;
  border: 1px solid var(--input-primary-border);
  transition: border-color 0.2s ease;
}

.phone-input-container:focus-within {
  border-color: var(--input-active-border);
}

.phone-number-input {
  width: 100%;
  min-height: 40px;
  border: none;
  outline: none;
  background: var(--theme-background-color);
  color: var(--theme-heading-color);
  padding: 8px 12px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.phone-number-input:focus {
  border-color: var(--input-active-border);
}

.phone-number-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.phone-number-input::placeholder {
  color: var(--theme-text-color);
  opacity: 0.6;
}

.country-dropdown-container {
  position: relative;
  flex-shrink: 0;
  z-index: 10000;
}

.country-dropdown-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  min-width: 100px;
  transition: background-color 0.2s ease;
  border-right: 1px solid var(--input-primary-border);
}

.country-dropdown-button:focus {
  outline: none;
}

.country-dropdown-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.country-flag {
  font-size: 16px;
}

.country-code {
  font-weight: 500;
  color: var(--theme-heading-color);
}

.dropdown-arrow {
  transition: transform 0.2s ease;
  color: var(--theme-heading-color);
  flex-shrink: 0;
}

.dropdown-arrow.arrow-up {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  min-width: 300px;
  background: var(--theme-background-color);
  border: 1px solid var(--input-primary-border);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  max-height: 300px;
  overflow: hidden;
}

.search-container {
  padding: 8px 16px;
  border-bottom: 1px solid var(--input-primary-border);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: var(--theme-text-color);
  opacity: 0.6;
  pointer-events: none;
  width: 16px;
  height: 16px;
}

.search-input {
  width: 100%;
  padding: 6px 8px 6px 32px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: var(--theme-text-color);
  transition: border-color 0.2s ease;
}

.search-input:focus {
  border-color: var(--input-active-border);
}

.country-list {
  max-height: 250px;
  overflow-y: auto;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-align: left;
}

.country-item.selected .country-name {
  color: theme('colors.white');;
}

.country-item.selected .country-code {
  color: theme('colors.white');
}

.country-item:hover {
  background-color: var(--theme-background-color);
}

.country-item.selected {
  background-color: var(--input-active-border);
  color: theme('colors.white');
}

.country-item.focused {
  background-color: var(--input-active-border);
  color: var(--theme-text-color)
}

.country-item.focused .country-name {
  color: var(--theme-background-color);
}

.country-item.focused .country-code {
  color: var(--theme-background-color);
}

.country-name {
  flex: 1;
  font-size: 14px;
  color: var(--theme-heading-color);
}

.phone-input-container:focus-within {
  border-color: var(--input-active-border);
}

.phone-input-container.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.phone-input-container.error {
  border-color: var(--input-error-border);
}

.phone-input-container.error:focus-within {
  border-color:  var(--input-error-border);
}

.phone-input-container.error .country-dropdown-button {
  border-right-color: var(--input-error-border);
}
</style>
